import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, User, Camera, Plus } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabase';
import WorkflowPhotoCapture from '../WorkflowPhotoCapture';

interface WorkflowStep {
  workflow_step_library_id: string;
  title: string;
  description?: string;
  order_index: number;
  status?: 'pending' | 'completed';
  completed_at?: string;
  photos_enabled?: boolean;
  assigned_resources?: Array<{
    id: string;
    name: string;
    avatar_url?: string;
  }>;
}

interface SimpleWorkflowDisplayProps {
  service: any; // BookingService with workflow_steps JSONB
  bookingStatus: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  bookingId?: string; // Add booking ID for photo storage
}

export default function SimpleWorkflowDisplay({
  service,
  bookingStatus,
  onUpdate,
  onError,
  bookingId
}: SimpleWorkflowDisplayProps) {
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [completingStep, setCompletingStep] = useState<string | null>(null);
  const [capturingPhotosForStep, setCapturingPhotosForStep] = useState<string | null>(null);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [selectedStepForPhotos, setSelectedStepForPhotos] = useState<WorkflowStep | null>(null);

  // Load workflow steps from JSONB column
  useEffect(() => {
    console.log('🔍 SimpleWorkflowDisplay - Checking service for workflow steps:', {
      serviceName: service.name || service.service_name,
      serviceId: service.id,
      hasWorkflowSteps: !!service.workflow_steps,
      workflowStepsType: typeof service.workflow_steps,
      workflowStepsData: service.workflow_steps,
      fullService: service
    });

    if (service.workflow_steps && Array.isArray(service.workflow_steps)) {
      const steps = service.workflow_steps.map((step: any, index: number) => ({
        ...step,
        id: step.workflow_step_library_id || `step_${index}`,
        status: step.status || 'pending',
        completed_at: step.completed_at || null,
        // Ensure we have the required fields
        title: step.title || 'Untitled Step',
        description: step.description || '',
        order_index: step.order_index || index + 1,
        assigned_resources: step.assigned_resources || [],
        photos_enabled: step.photos_enabled || false
      }));
      setWorkflowSteps(steps);
      console.log('📋 Loaded workflow steps for service:', service.name || service.service_name, steps);
      console.log('📸 Photos enabled status:', steps.map(step => ({
        title: step.title,
        photos_enabled: step.photos_enabled,
        hasPhotosField: 'photos_enabled' in step
      })));
    } else {
      setWorkflowSteps([]);
      console.log('❌ No workflow steps found for service:', service.name || service.service_name);
    }
  }, [service]);

  // Handle photo capture for a workflow step
  const handlePhotoCapture = (step: WorkflowStep) => {
    console.log('📸 Camera icon clicked for step:', step.title);
    console.log('📸 Booking ID available:', !!bookingId);
    console.log('📸 Step details:', { id: step.id, title: step.title, photos_enabled: step.photos_enabled });
    console.log('📸 Current showPhotoCapture state:', showPhotoCapture);
    console.log('📸 About to set showPhotoCapture to true');
    
    if (!bookingId) {
      console.error('❌ No booking ID provided for photo capture');
      onError('Booking ID is required for photo capture');
      return;
    }
    
    setSelectedStepForPhotos(step);
    setShowPhotoCapture(true);
    
    // Add a slight delay to check if state updated
    setTimeout(() => {
      console.log('📸 showPhotoCapture state after update:', showPhotoCapture);
    }, 100);
  };

  // Handle photo capture modal close
  const handlePhotoCaptureClose = () => {
    setShowPhotoCapture(false);
    setSelectedStepForPhotos(null);
  };

  // Handle photos updated
  const handlePhotosUpdated = async () => {
    // Refresh the workflow display if needed
    if (onUpdate) {
      await onUpdate();
    }
  };

  // Handle completing a workflow step
  const handleCompleteStep = async (step: WorkflowStep) => {
    try {
      // Business rule: Only allow completion when booking is in progress
      if (bookingStatus !== 'in_progress') {
        onError('Workflow steps can only be completed when the booking is in progress');
        return;
      }

      setCompletingStep(step.id);
      console.log('🎯 Completing step:', step.title);
      
      // Update the step status in the JSONB column
      const updatedSteps = workflowSteps.map(s => 
        s.id === step.id 
          ? { ...s, status: 'completed' as const, completed_at: new Date().toISOString() }
          : s
      );

      // Update the booking_services table with the new workflow_steps data
      const { error } = await supabase
        .from('booking_services')
        .update({ workflow_steps: updatedSteps })
        .eq('id', service.id);

      if (error) throw error;

      // Update local state
      setWorkflowSteps(updatedSteps);
      
      // Refresh the parent component
      await onUpdate();
      
      console.log('✅ Step completed successfully');
    } catch (error) {
      console.error('❌ Error completing step:', error);
      onError('Failed to complete workflow step');
    } finally {
      setCompletingStep(null);
    }
  };

  // Don't render if no workflow steps
  if (!workflowSteps || workflowSteps.length === 0) {
    console.log('🚫 SimpleWorkflowDisplay: Not rendering - no workflow steps');
    return null;
  }

  console.log('✅ SimpleWorkflowDisplay: Rendering', workflowSteps.length, 'workflow steps');

  return (
    <div className="mt-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">Workflow</h4>
        <button className="text-xs text-purple-600 hover:text-purple-700 font-medium">
          Edit Workflow
        </button>
      </div>

      {/* Workflow Steps */}
      <div className="space-y-2">
        {workflowSteps.map((step, index) => {
          const isCompleted = step.status === 'completed';
          const isLoading = completingStep === step.id;

          return (
            <div 
              key={step.id} 
              className={`flex items-start p-3 rounded-lg border transition-all ${
                isCompleted 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              {/* Step Icon */}
              <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 flex-shrink-0 ${
                isCompleted 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white border-2 border-gray-300 text-gray-600'
              }`}>
                {isCompleted ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <span className="text-xs font-semibold">{index + 1}</span>
                )}
              </div>

              {/* Step Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Title */}
                    <div className="flex items-center gap-2">
                      <h5 className={`text-sm font-medium ${
                        isCompleted ? 'text-green-700' : 'text-gray-900'
                      }`}>
                        {step.title}
                      </h5>
                      {step.photos_enabled && (
                        <button
                          onClick={() => handlePhotoCapture(step)}
                          disabled={capturingPhotosForStep === step.id}
                          className="flex items-center justify-center w-5 h-5 rounded bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700 transition-colors cursor-pointer disabled:opacity-50"
                          title="Click to capture photos"
                        >
                          {capturingPhotosForStep === step.id ? (
                            <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Camera className="h-3 w-3" />
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Description */}
                    {step.description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {step.description}
                      </p>
                    )}

                    {/* Completion timestamp */}
                    {isCompleted && step.completed_at && (
                      <p className="text-xs text-green-600 mt-1">
                        Completed {new Date(step.completed_at).toLocaleDateString()} at {new Date(step.completed_at).toLocaleTimeString()}
                      </p>
                    )}

                    {/* Assigned Resources */}
                    {step.assigned_resources && step.assigned_resources.length > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        {step.assigned_resources.map((resource, resIndex) => (
                          <div key={resIndex} className="flex items-center">
                            <div className="w-5 h-5 rounded bg-gray-300 overflow-hidden border border-white">
                              {resource.avatar_url ? (
                                <img
                                  src={resource.avatar_url}
                                  alt={resource.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-purple-500 text-white text-xs font-bold">
                                  {resource.name.split(' ').map(n => n[0]).join('')}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Complete Button */}
                  {!isCompleted && (
                    <button 
                      onClick={() => handleCompleteStep(step)}
                      disabled={isLoading}
                      className={`ml-3 px-3 py-1 text-xs font-medium rounded transition-colors ${
                        isLoading
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          <span>...</span>
                        </div>
                      ) : (
                        'Complete'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Photo Capture Modal */}
      {showPhotoCapture && selectedStepForPhotos && bookingId && createPortal(
        <WorkflowPhotoCapture
          isOpen={true}
          onClose={handlePhotoCaptureClose}
          stepTitle={selectedStepForPhotos.title}
          stepId={selectedStepForPhotos.id}
          bookingId={bookingId}
          serviceId={service.id || service.service_id || ''}
          onPhotosUpdated={handlePhotosUpdated}
        />,
        document.body
      )}
    </div>
  );
}
